import { useEffect, useState, useCallback } from "react";
import AppLayout from "@/components/AppLayout";
import { AdminSidebar } from "@/components/sidebars";
import { apiClient, formatApiError } from "@/lib/api";
import { toast } from "sonner";

const NAVY = "#132a63";
const GREEN = "#16a34a";

const PLATFORMS = [
  { v: "instagram", label: "Instagram", icon: "fab fa-instagram" },
  { v: "tiktok", label: "TikTok", icon: "fab fa-tiktok" },
  { v: "youtube", label: "YouTube", icon: "fab fa-youtube" },
  { v: "otro", label: "Otro", icon: "fas fa-globe" },
];
const NICHOS = [
  { v: "medico", label: "Salud" }, { v: "legal", label: "Legal" }, { v: "gruas", label: "Autos / Vial" },
  { v: "familia", label: "Familia" }, { v: "finanzas", label: "Finanzas" }, { v: "otro", label: "Otro" },
];
const STATUS_LABEL = {
  pending: { t: "Pendiente", c: "#6b7280" }, contacted: { t: "Contactado", c: "#2563eb" },
  opened: { t: "Abrió", c: "#7c3aed" }, registered: { t: "Registrado", c: GREEN },
  active: { t: "Activo 💰", c: "#059669" }, declined: { t: "Rechazó", c: "#b91c1c" },
  bounced: { t: "Rebotó", c: "#b91c1c" },
};
const platIcon = (p) => (PLATFORMS.find((x) => x.v === p) || PLATFORMS[3]).icon;

export default function AdminInfluencers() {
  const [rows, setRows] = useState([]);
  const [stats, setStats] = useState({});
  const [total, setTotal] = useState(0);
  const [filter, setFilter] = useState("");
  const [detail, setDetail] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    const q = filter ? `?status=${filter}` : "";
    apiClient.get(`/outreach/prospects${q}`).then(({ data }) => {
      setRows(data.rows); setStats(data.stats || {}); setTotal(data.total);
    }).catch(() => {});
  }, [filter]);
  useEffect(load, [load]);

  return (
    <AppLayout sidebar={AdminSidebar}>
      <header className="app-header">
        <h1>Reclutamiento de Influencers</h1>
        <p className="text-muted">{total} prospecto(s) · El robot contacta por email con propuestas personalizadas por IA</p>
      </header>

      <div className="panel" style={{ display: "flex", gap: 10, flexWrap: "wrap", marginBottom: 16 }} data-testid="outreach-stats">
        {Object.entries(STATUS_LABEL).map(([k, v]) => (
          <div key={k} style={{ ...pill, borderColor: v.c }} data-testid={`stat-${k}`}>
            <span style={{ color: v.c, fontWeight: 800, fontSize: 20 }}>{stats[k] || 0}</span>
            <span style={{ fontSize: 12, color: "#6b7280" }}>{v.t}</span>
          </div>
        ))}
      </div>

      <ImportPanel onDone={load} />
      <AddOne onDone={load} />

      <div className="panel">
        <div style={{ display: "flex", gap: 10, marginBottom: 12 }}>
          <select value={filter} onChange={(e) => setFilter(e.target.value)} data-testid="outreach-filter">
            <option value="">Todos los estados</option>
            {Object.entries(STATUS_LABEL).map(([k, v]) => <option key={k} value={k}>{v.t}</option>)}
          </select>
        </div>
        <table className="data-table">
          <thead><tr><th>Plataforma</th><th>Handle</th><th>Nombre</th><th>Email</th><th>Nicho</th><th>Seguidores</th><th>Estado</th><th>Acciones</th></tr></thead>
          <tbody>
            {rows.map((p) => (
              <tr key={p.id} data-testid={`prospect-${p.id}`}>
                <td><i className={platIcon(p.platform)} /> {p.platform}</td>
                <td><strong>@{p.handle}</strong></td>
                <td>{p.nombre || "—"}</td>
                <td>{p.email || <span style={{ color: "#b91c1c" }}>sin email</span>}</td>
                <td>{(NICHOS.find((n) => n.v === p.nicho) || {}).label || p.nicho}</td>
                <td>{p.seguidores || "—"}</td>
                <td><span style={{ color: (STATUS_LABEL[p.status] || {}).c, fontWeight: 700 }}>{(STATUS_LABEL[p.status] || {}).t || p.status}</span></td>
                <td>
                  <button className="btn-mini" data-testid={`prospect-open-${p.id}`} onClick={() => setDetail(p)}>
                    <i className="fas fa-wand-magic-sparkles" /> Ver / Enviar
                  </button>
                </td>
              </tr>
            ))}
            {rows.length === 0 && <tr><td colSpan={8} style={{ textAlign: "center", color: "#9ca3af", padding: 24 }}>Aún no hay prospectos. Importa una lista o agrega uno arriba.</td></tr>}
          </tbody>
        </table>
      </div>

      {detail && <DetailModal prospect={detail} busy={busy} setBusy={setBusy} onClose={() => setDetail(null)} onChanged={load} />}
    </AppLayout>
  );
}

function ImportPanel({ onDone }) {
  const [open, setOpen] = useState(false);
  const [text, setText] = useState("");
  const parse = () => {
    const rows = [];
    text.split("\n").map((l) => l.trim()).filter(Boolean).forEach((line) => {
      const [handle, platform, nombre, email, nicho, seguidores] = line.split(",").map((s) => (s || "").trim());
      if (handle) rows.push({ handle, platform: platform || "instagram", nombre, email, nicho: nicho || "otro", seguidores });
    });
    return rows;
  };
  const submit = async () => {
    const rows = parse();
    if (!rows.length) return toast.error("No detecté filas válidas.");
    try {
      const { data } = await apiClient.post("/outreach/prospects/import", { rows });
      toast.success(`Importados ${data.created} · omitidos ${data.skipped}`);
      setText(""); setOpen(false); onDone();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || e.message); }
  };
  return (
    <div className="panel" style={{ marginBottom: 16 }}>
      <button className="btn-mini" onClick={() => setOpen(!open)} data-testid="import-toggle">
        <i className="fas fa-file-import" /> Importar lista (CSV)
      </button>
      {open && (
        <div style={{ marginTop: 12 }}>
          <p style={{ fontSize: 13, color: "#6b7280", marginBottom: 6 }}>
            Una línea por influencer, separado por comas:<br />
            <code>handle, plataforma, nombre, email, nicho, seguidores</code><br />
            Ej: <code>doctora_ana, tiktok, Dra. Ana, ana@mail.com, medico, 50k-100k</code>
          </p>
          <a href="/socios/influencers_ejemplo.csv" download data-testid="import-example-csv"
            style={{ fontSize: 13, color: GREEN, fontWeight: 700, textDecoration: "none", display: "inline-block", marginBottom: 8 }}>
            <i className="fas fa-download" /> Descargar CSV de ejemplo
          </a>
          <textarea data-testid="import-textarea" value={text} onChange={(e) => setText(e.target.value)} rows={5}
            style={{ width: "100%", padding: 10, borderRadius: 8, border: "1px solid #cbd5e1", fontFamily: "monospace", fontSize: 13, boxSizing: "border-box" }} />
          <button className="btn-mini" style={{ background: GREEN, color: "white", marginTop: 8 }} onClick={submit} data-testid="import-submit">
            Importar
          </button>
        </div>
      )}
    </div>
  );
}

function AddOne({ onDone }) {
  const [open, setOpen] = useState(false);
  const [f, setF] = useState({ platform: "instagram", handle: "", nombre: "", email: "", nicho: "medico", seguidores: "" });
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const submit = async () => {
    if (!f.handle.trim()) return toast.error("El handle es obligatorio.");
    try {
      await apiClient.post("/outreach/prospects", f);
      toast.success("Prospecto agregado");
      setF({ platform: "instagram", handle: "", nombre: "", email: "", nicho: "medico", seguidores: "" });
      setOpen(false); onDone();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || e.message); }
  };
  return (
    <div className="panel" style={{ marginBottom: 16 }}>
      <button className="btn-mini" onClick={() => setOpen(!open)} data-testid="addone-toggle"><i className="fas fa-plus" /> Agregar prospecto</button>
      {open && (
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(140px,1fr))", gap: 8, marginTop: 12 }}>
          <select value={f.platform} onChange={set("platform")} data-testid="addone-platform">{PLATFORMS.map((p) => <option key={p.v} value={p.v}>{p.label}</option>)}</select>
          <input placeholder="@handle" value={f.handle} onChange={set("handle")} data-testid="addone-handle" style={inp} />
          <input placeholder="Nombre" value={f.nombre} onChange={set("nombre")} data-testid="addone-nombre" style={inp} />
          <input placeholder="email" value={f.email} onChange={set("email")} data-testid="addone-email" style={inp} />
          <select value={f.nicho} onChange={set("nicho")} data-testid="addone-nicho">{NICHOS.map((n) => <option key={n.v} value={n.v}>{n.label}</option>)}</select>
          <input placeholder="seguidores" value={f.seguidores} onChange={set("seguidores")} data-testid="addone-seguidores" style={inp} />
          <button className="btn-mini" style={{ background: GREEN, color: "white" }} onClick={submit} data-testid="addone-submit">Guardar</button>
        </div>
      )}
    </div>
  );
}

function DetailModal({ prospect, busy, setBusy, onClose, onChanged }) {
  const [p, setP] = useState(prospect);
  const [paras, setParas] = useState((prospect.ai_paragraphs || []).join("\n\n"));
  const [subject, setSubject] = useState(prospect.ai_subject || "");
  const splitParas = () => paras.split(/\n\n+/).map((s) => s.trim()).filter(Boolean);

  const generate = async () => {
    setBusy(true);
    try {
      const { data } = await apiClient.post(`/outreach/prospects/${p.id}/generate`);
      setSubject(data.subject); setParas(data.paragraphs.join("\n\n"));
      toast.success("Propuesta generada con IA ✨");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || e.message); }
    finally { setBusy(false); }
  };
  const saveEdits = async () => {
    await apiClient.put(`/outreach/prospects/${p.id}`, { ai_subject: subject, ai_paragraphs: splitParas() });
  };
  const send = async () => {
    if (!p.email) return toast.error("Este prospecto no tiene email.");
    setBusy(true);
    try {
      await apiClient.put(`/outreach/prospects/${p.id}`, { ai_subject: subject, ai_paragraphs: splitParas() });
      const { data } = await apiClient.post(`/outreach/prospects/${p.id}/send`);
      if (data.status === "sent") toast.success("Propuesta enviada por email 📧");
      else toast.error(`No se envió: ${data.status}`);
      onChanged(); onClose();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || e.message); }
    finally { setBusy(false); }
  };
  const del = async () => {
    if (!window.confirm("¿Eliminar este prospecto?")) return;
    await apiClient.delete(`/outreach/prospects/${p.id}`);
    toast.success("Eliminado"); onChanged(); onClose();
  };

  return (
    <div style={overlay} onClick={onClose} data-testid="prospect-modal">
      <div style={modal} onClick={(e) => e.stopPropagation()}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <h2 style={{ margin: 0, color: NAVY }}><i className={platIcon(p.platform)} /> @{p.handle}</h2>
          <button className="btn-mini" onClick={onClose} data-testid="modal-close">✕</button>
        </div>
        <p style={{ color: "#6b7280", fontSize: 13, margin: "4px 0 16px" }}>
          {p.nombre || "—"} · {p.email || "sin email"} · {p.seguidores || "—"} seguidores
        </p>

        <label style={lbl}>Asunto del correo</label>
        <input value={subject} onChange={(e) => setSubject(e.target.value)} style={inp} data-testid="modal-subject" />

        <label style={lbl}>Mensaje (editable)</label>
        <textarea value={paras} onChange={(e) => setParas(e.target.value)} rows={8} data-testid="modal-message"
          style={{ width: "100%", padding: 10, borderRadius: 8, border: "1px solid #cbd5e1", fontSize: 14, boxSizing: "border-box" }} />

        <div style={{ display: "flex", gap: 10, marginTop: 16, flexWrap: "wrap" }}>
          <button className="btn-mini" onClick={generate} disabled={busy} data-testid="modal-generate">
            <i className="fas fa-wand-magic-sparkles" /> {busy ? "..." : "Generar con IA"}
          </button>
          <button className="btn-mini" onClick={async () => { await saveEdits(); toast.success("Guardado"); }} data-testid="modal-save">
            <i className="fas fa-save" /> Guardar edición
          </button>
          <button className="btn-mini" style={{ background: GREEN, color: "white" }} onClick={send} disabled={busy} data-testid="modal-send">
            <i className="fas fa-paper-plane" /> Enviar propuesta por email
          </button>
          <button className="btn-mini" style={{ marginLeft: "auto", color: "#b91c1c" }} onClick={del} data-testid="modal-delete">
            <i className="fas fa-trash" /> Eliminar
          </button>
        </div>
      </div>
    </div>
  );
}

const pill = { border: "2px solid", borderRadius: 12, padding: "8px 16px", display: "flex", flexDirection: "column", alignItems: "center", minWidth: 80 };
const inp = { width: "100%", padding: "10px 12px", borderRadius: 8, border: "1px solid #cbd5e1", fontSize: 14, boxSizing: "border-box" };
const lbl = { display: "block", fontSize: 12, fontWeight: 700, color: NAVY, marginTop: 12, marginBottom: 4 };
const overlay = { position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000, padding: 16 };
const modal = { background: "white", borderRadius: 16, padding: 24, maxWidth: 560, width: "100%", maxHeight: "90vh", overflowY: "auto" };
